import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import Select from '@mui/material/Select'
import isEmpty from 'lodash/isEmpty'
import { t } from '../../i18n'
import OrderProviderForm, { CredentialField } from './OrderProviderForm'
import { ListItemText, MenuItem, OutlinedInput } from '@mui/material'

interface OrderProvider {
  id?: number
  provider_type: string
  credentials: {}
  connected: boolean
  destroyable: boolean
  robo_api_key_set: boolean
  _destroy?: boolean
}

interface OrganizationFormProps {
  organization?: {
    id?: number
    name?: string
    code?: string
    address?: string
    order_providers_attributes: OrderProvider[]
    order_providers?: OrderProvider[]
  }
  onSubmitForm: (values: any, callback: (success: boolean, errorData: any) => void) => void
}

interface OrderProviderFormSubmitted {
  provider_type: string
  isSubmitted: boolean
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  organization = {},
  onSubmitForm
 }) => {

  const [orderProviderOptions, setOrderProviderOptions] = useState<string[]>([])
  const [orderProviderFields, setOrderProviderFields] = useState<Record<string, CredentialField[]>>({})
  const [selectedOrderProviders, setSelectedOrderProviders] = useState<OrderProvider[]>([])
  const [orderProvidersSubmitted, setOrderProvidersSubmitted] = useState<OrderProviderFormSubmitted[]>([])
  const [orderProvidersConnectErrors, setOrderProvidersConnectErrors] = useState<{}>({})

  useEffect(() => {
    fetchOrderProviderOptions()
    fetchOrderProviderFields()
    setSelectedOrderProviders(organization.order_providers_attributes || [])
  }, [])

  const setOrderProviderSubmitted = (provider_type: string, isSubmitted: boolean) => {
    const updatedOrderProvidersSubmitted = orderProvidersSubmitted.map((provider) => {
      if (provider.provider_type === provider_type) {
        return { ...provider, isSubmitted }
      }
      return provider
    })

    setOrderProvidersSubmitted(updatedOrderProvidersSubmitted)
  }

  const allOrderProvidersSubmitted = () => {
    return orderProvidersSubmitted.filter(
      (providerSubmitted) => selectedOrderProviders.some((provider) => provider.provider_type === providerSubmitted.provider_type)
    ).every((provider) => provider.isSubmitted)
  }

  const validationSchema = Yup.object({
    name: Yup.string().required(t('organizations.validations.name')),
    code: Yup.string().required(t('organizations.validations.code'))
  })

  const fetchOrderProviderOptions = () => {
    axios.get('/admin/order_providers/provider_options').then((resp) => {
      setOrderProviderOptions([...resp.data, 'clear'])

      const initialOrderProvidersSubmitted = resp.data.map((provider) => ({
        provider_type: provider,
        isSubmitted: organization.order_providers?.some((p) => p.provider_type === provider) || false,
      }))
      setOrderProvidersSubmitted(initialOrderProvidersSubmitted)
    })
  }

  const fetchOrderProviderFields = () => {
    axios.get('/admin/order_providers/required_fields')
    .then((resp) => {
      setOrderProviderFields(resp.data)
    })
  }

  const connectOrderProvider = (orderProviderId: string | number, setIsConnecting) => {
    axios.post(`/admin/order_providers/${orderProviderId}/connect`).then((response) => {
      setOrderProvidersConnectErrors((prevErrors: { [key: string]: any }) => {
        const { [orderProviderId]: _, ...remainingErrors } = prevErrors;
        return remainingErrors;
      });

      const updated = selectedOrderProviders.map((provider) => {
        if (provider.id === orderProviderId) {
          return {...provider, connected: true}
        }
        return provider
      })

      setSelectedOrderProviders(updated)
    }
    ).catch((error) => {
      setOrderProvidersConnectErrors((prevErrors) => ({
        ...prevErrors,
        [orderProviderId]: error.response.data.error,
      }))
      const updated = selectedOrderProviders.map((provider) => {
        if (provider.id === orderProviderId) {
          return {...provider, connected: false}
        }
        return provider
      })

      setSelectedOrderProviders(updated)
    }).finally(() => {
      setIsConnecting(false)
    })
  }

  const renderProviderOptionsSelectedValue = (selected) => {
    if (selected.length > 0) {
      return selected.join(', ')
    } else {
      return t('order_providers.select_order_providers')
    }
  }

  const renderProviderSelect = () => {
    if (!orderProviderOptions || orderProviderOptions.length === 0) return null;

    const handleProviderChange = (event) => {
      const selectedValues = event.target.value;

      const updatedProviders = orderProviderOptions.filter(provider => provider != 'clear')
        .map((providerType) => {
        const existingProvider = selectedOrderProviders.find(
          (provider) => provider.provider_type === providerType
        );

        if (selectedValues.includes(providerType)) {
          if (existingProvider) {
            return { ...existingProvider, _destroy: false };
          } else {
            return {
              provider_type: providerType,
              id: undefined,
              credentials: {},
              connected: false,
              destroyable: true,
              _destroy: false,
            };
          }
        } else {
          if (existingProvider) {
            setOrderProvidersSubmitted((prev) => {
              return prev.map((provider) => {
                if (provider.provider_type === providerType) {
                  return { ...provider, isSubmitted: true }
                }
                return provider
              })
            })
            return { ...existingProvider, _destroy: true };
          }
        }

        return null;
      }).filter(Boolean);

      setSelectedOrderProviders(updatedProviders);
    };

    return (
      <>
        <h5 className="mt-5">Order providers</h5>
        <div className="form-group">
          <Select
            className="mt-3"
            value={selectedOrderProvidersValue()}
            onChange={handleProviderChange}
            displayEmpty={true}
            input={<OutlinedInput />}
            disabled={
              selectedOrderProviders.some(
                (provider) => provider.destroyable === false)
            }
            renderValue={renderProviderOptionsSelectedValue}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  width: 250,
                },
              },
            }}
            sx={{
              color: selectedOrderProviders.length > 0 ? 'black' : '#757575',
              width: '250px',
              height: '40px',
              backgroundColor: 'white'
            }}
          >
            {orderProviderOptions.map((option) => (
              <MenuItem
                key={option}
                value={option}
              >
                <ListItemText primary={option} />
              </MenuItem>
            ))}
          </Select>
        </div>
      </>
    );
  };

  const selectedOrderProvidersValue = () => {
    const value = selectedOrderProviders
      .filter((provider) => !provider._destroy)
      .map((provider) => provider.provider_type)

    return value.length == 0 ? '' : value
  }

  const updateSelectedOrderProviderFields = (orderProvider) => {
    setSelectedOrderProviders((prev) => {
      const updated = prev.map((provider) => {
        if (provider.provider_type === orderProvider.provider_type) {
          return orderProvider
        }
        return provider
      })
      return updated
    })
  }


  return (
    <Formik
      initialValues={{
        name: organization.name || '',
        code: organization.code || '',
        address: organization.address || '',
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setStatus, setSubmitting }) => {
        const payload = {
          ...values,
          order_providers_attributes: selectedOrderProviders,
        }
        return (
          onSubmitForm(payload, (success, errorData) => {
            let status = {}
            if (!success && errorData?.full_errors) {
              const fields = Object.getOwnPropertyNames(errorData.full_errors)
              for (let key in fields) {
                status[`${fields[key]}_error`] =
                  errorData.full_errors[fields[key]].join(', ')
              }
            }
            setStatus(status)
            setSubmitting(false)
          })
        )
      }}
    >
      {({
        values,
        errors,
        touched,
        status,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setStatus,
      }) => {
        const enhancedHandleChange = (e) => {
          let newStatus = Object.assign({}, status || {})
          newStatus[`${e.target.name}_error`] = ''
          setStatus(newStatus)
          handleChange(e)
        }

        const renderField = (label, name, type, placeholder, autoComplete = 'off') => {
          const errorsPresent =
            (errors[name] && touched[name]) || (status && status[`${name}_error`])
          return (
            <div className="form-group">
              <label>{label}</label>
              <input
                id={name}
                name={name}
                type={type}
                onBlur={handleBlur}
                value={values[name] === null ? '' : values[name]}
                placeholder={placeholder}
                autoComplete={autoComplete}
                onChange={enhancedHandleChange}
                className={`form-control inverted ${
                  errorsPresent && 'is-invalid'
                }`}
              />
              {errorsPresent && (
                <div className="invalid-feedback">
                  {errors[name] || (status && status[`${name}_error`])}
                </div>
              )}
            </div>
          )
        }

        return (
          <form onSubmit={handleSubmit} className="form-robis">
            {renderField(t('organizations.name'), 'name', 'text', t('organizations.name'))}
            {renderField(t('organizations.code'), 'code', 'text', t('organizations.code'))}
            {renderField(t('organizations.address'), 'address', 'text', t('organizations.address'))}
            {renderProviderSelect()}

          {!isEmpty(orderProviderFields) && selectedOrderProviders.filter(
            (provider) => !provider._destroy).map((provider) => {
            return (
              <OrderProviderForm
                orderProvider={provider}
                key={provider.provider_type}
                requiredFields={orderProviderFields[provider.provider_type] || []}
                onSubmit={updateSelectedOrderProviderFields}
                setIsSubmitted={(submitted) => setOrderProviderSubmitted(provider.provider_type, submitted)}
                isSubmitted={orderProvidersSubmitted.find(
                  (p) => p.provider_type === provider.provider_type
                )?.isSubmitted || false}
                connectOrderProvider={connectOrderProvider}
                connectError={!!provider.id ? orderProvidersConnectErrors[provider.id] : null}
               />
            )
          })}

            <div className="form-actions d-flex justify-content-end">
              <a className="btn btn-secondary inverted me-2" href="/admin/organizations">
                {t('cancel')}
              </a>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting || !allOrderProvidersSubmitted()}>
                {t('save')}
              </button>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

export default OrganizationForm
